import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor, isAdmin, canWrite } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import AcceptInvite from "@/pages/AcceptInvite";
import Documents from "@/pages/Documents";
import UsersPage from "@/pages/UsersPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Packages from "@/pages/admin/Packages";
import Storage from "@/pages/admin/Storage";
import Studios from "@/pages/admin/Studios";
import Companies from "@/pages/admin/Companies";
import Requests from "@/pages/admin/Requests";
import StudioCompanies from "@/pages/studio/StudioCompanies";
import StudioArchive from "@/pages/studio/StudioArchive";
import Dashboard from "@/pages/company/Dashboard";
import Invoices from "@/pages/company/Invoices";
import InvoiceEditor from "@/pages/company/InvoiceEditor";
import InvoiceDetail from "@/pages/company/InvoiceDetail";
import Customers from "@/pages/company/Customers";
import Products from "@/pages/company/Products";
import Settings from "@/pages/company/Settings";
import Legal from "@/pages/Legal";
import Attivazione from "@/pages/Attivazione";

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};
import DdtList from "@/pages/company/DdtList";
import Incassi from "@/pages/company/Incassi";
import Catalogo from "@/pages/company/Catalogo";
import Ordini from "@/pages/company/Ordini";
import OrdineEditor from "@/pages/company/OrdineEditor";
import Preventivi from "@/pages/company/Preventivi";
import Proforma from "@/pages/company/Proforma";
import Prova from "@/pages/Prova";
import Partner from "@/pages/Partner";
import Funzione from "@/pages/Funzione";
import Visite from "@/pages/admin/Visite";
import Manuali from "@/pages/admin/Manuali";
import PrimaNota from "@/pages/company/PrimaNota";
import StudioPrimaNota from "@/pages/studio/StudioPrimaNota";
import { VisitTracker } from "@/components/VisitTracker";
import Provvigioni from "@/pages/company/Provvigioni";
import CustomerCard from "@/pages/company/CustomerCard";
import DdtEditor from "@/pages/company/DdtEditor";
import Magazzino from "@/pages/company/Magazzino";
import Corrispettivi from "@/pages/company/Corrispettivi";

const AGENT_PATHS = /^\/app\/(incassi|fatture\/(?!nuova$)[\w-]+|ordini(\/nuovo|\/[\w-]+)?|preventivi(\/nuovo|\/[\w-]+)?|ddt(\/[\w-]+)?|anagrafiche(\/[\w-]+)?|catalogo|provvigioni)\/?$/;

function Guard({ role }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user.role !== role) return <Navigate to={homeFor(user)} replace />;
  if (user.tenant_role === "agente" && !AGENT_PATHS.test(loc.pathname)) return <Navigate to="/app/incassi" replace />;
  return <AppShell />;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return null;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

function Only({ admin, write, nodemo, children }) {
  const { user } = useAuth();
  if ((admin && !isAdmin(user)) || (write && !canWrite(user)) || (nodemo && user.demo)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <VisitTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/cookie" element={<Legal kind="cookie" />} />
          <Route path="/termini" element={<Legal kind="termini" />} />
          <Route path="/attivazione/:pkgId" element={<Attivazione />} />
          <Route path="/prova" element={<Prova />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/funzioni/:slug" element={<Funzione />} />
          <Route path="/login" element={<Login />} />
          <Route path="/attiva" element={<AcceptInvite />} />
          <Route path="/admin" element={<Guard role="superadmin" />}>
            <Route index element={<AdminDashboard />} />
            <Route path="pacchetti" element={<Packages />} />
            <Route path="spazio" element={<Storage />} />
            <Route path="studi" element={<Studios />} />
            <Route path="aziende" element={<Companies />} />
            <Route path="richieste" element={<Requests />} />
            <Route path="visite" element={<Visite />} />
            <Route path="manuali" element={<Manuali />} />
            <Route path="utenti" element={<UsersPage />} />
          </Route>
          <Route path="/studio" element={<Guard role="studio" />}>
            <Route index element={<StudioCompanies />} />
            <Route path="archivio" element={<StudioArchive />} />
            <Route path="primanota" element={<StudioPrimaNota />} />
            <Route path="documenti" element={<Documents />} />
            <Route path="utenti" element={<Only admin nodemo><UsersPage /></Only>} />
          </Route>
          <Route path="/app" element={<Guard role="company" />}>
            <Route index element={<Dashboard />} />
            <Route path="fatture" element={<Invoices direction="emessa" key="out" />} />
            <Route path="fatture/nuova" element={<Only write><InvoiceEditor key="new" /></Only>} />
            <Route path="fatture/:id" element={<InvoiceDetail />} />
            <Route path="fatture/:id/modifica" element={<Only write><InvoiceEditor key="edit" /></Only>} />
            <Route path="ricevute" element={<Invoices direction="ricevuta" key="in" />} />
            <Route path="ricevute/:id" element={<InvoiceDetail />} />
            <Route path="anagrafiche" element={<Customers />} />
            <Route path="anagrafiche/:id" element={<CustomerCard />} />
            <Route path="ddt" element={<DdtList />} />
            <Route path="incassi" element={<Incassi />} />
            <Route path="catalogo" element={<Catalogo />} />
            <Route path="ordini" element={<Ordini />} />
            <Route path="ordini/nuovo" element={<OrdineEditor />} />
            <Route path="ordini/:id" element={<OrdineEditor />} />
            <Route path="preventivi" element={<Preventivi />} />
            <Route path="proforma" element={<Proforma />} />
            <Route path="proforma/nuova" element={<Only write><InvoiceEditor proforma key="pfn" /></Only>} />
            <Route path="proforma/:id" element={<Only write><InvoiceEditor proforma key="pfe" /></Only>} />
            <Route path="primanota" element={<PrimaNota />} />
            <Route path="preventivi/nuovo" element={<OrdineEditor kind="preventivi" key="pn" />} />
            <Route path="preventivi/:id" element={<OrdineEditor kind="preventivi" key="pe" />} />
            <Route path="provvigioni" element={<Provvigioni />} />
            <Route path="ddt/nuovo" element={<Only write><DdtEditor key="new" /></Only>} />
            <Route path="ddt/:id" element={<Only write><DdtEditor key="edit" /></Only>} />
            <Route path="articoli" element={<Products />} />
            <Route path="magazzino" element={<Magazzino />} />
            <Route path="corrispettivi" element={<Corrispettivi />} />
            <Route path="documenti" element={<Documents />} />
            <Route path="impostazioni" element={<Only admin><Settings /></Only>} />
            <Route path="utenti" element={<Only admin nodemo><UsersPage /></Only>} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="bottom-right" />
    </AuthProvider>
  );
}

export default App;
